import { createContext, useContext, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { EvidenceSpan, ProductionPlanView } from '@scoutaround/script-breakdown-shared';

import { ApiError } from '../../lib/api.js';
import { useMembership } from '../../lib/permissions.js';
import type { PlanNavigate } from './address.js';

/**
 * Is the production plan on for this production?
 *
 * Read from `/me`'s `features.productionPlan`, the server's
 * `PRODUCTION_REASONING` switch. Like the conversations switch, UNKNOWN reads
 * OFF: while it is off nothing of the plan is drawn and its query never runs,
 * and the endpoints answer 404 `PRODUCTION_PLAN_OFF` anyway.
 */
export function useProductionPlanEnabled(): boolean {
  return useMembership()?.features?.productionPlan === true;
}

export function productionPlanKey(projectId: string, scriptVersionId: string) {
  return ['production-plan', projectId, scriptVersionId] as const;
}

export interface PlanContextValue {
  readonly projectId: string;
  readonly scriptVersionId: string;
  /** The plan as the server answered it; null while loading or after a failure. */
  readonly plan: ProductionPlanView | null;
  readonly loading: boolean;
  /** Why the plan could not be read, in words. */
  readonly error: string | null;
  /** "Scene 12 · INT. HARBOUR — NIGHT" — the scenes this draft has, by id. */
  readonly sceneLabel: (sceneId: string) => string;
  readonly navigate: PlanNavigate;
  /** `?req=` — the requirement a link pointed at. */
  readonly focusRequirementId: string | null;
  /** The Locations workflow for a location need; absent, no link is offered. */
  readonly onOpenLocationRequirement?: (requirementId: string) => void;
  /**
   * Put a quote in view in the screenplay reader beside the plan, and pulse it — the reader's own
   * "Show in script" path. Set by the scene breakdown workspace while its reader is on screen, and
   * offered only for a quote from the scene in view (`EvidenceQuotes`' `sceneId`). Absent — the
   * `?view=plan` overview, which has no reader — every quote stays text, exactly as before.
   */
  readonly showEvidence?: (span: EvidenceSpan) => void;
}

const PlanContext = createContext<PlanContextValue | null>(null);

/**
 * Always rendered, with `null` while the plan is off, so switching it on or off
 * never remounts the screen underneath (and loses its scene and ticks).
 */
export function ProductionPlanProvider({ value, children }: { value: PlanContextValue | null; children: ReactNode }) {
  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}

export function usePlan(): PlanContextValue {
  const value = useContext(PlanContext);
  if (value === null) throw new Error('A production plan view rendered outside its provider');

  return value;
}

/**
 * ONE WRITE TO THE PLAN, re-reading it afterwards.
 *
 * Every decision presents the row version it was made against; a refusal for a
 * stale row (409) re-reads the plan too, so the person decides again with the
 * current row in front of them. The server's sentence is the error shown.
 */
export function usePlanWrite<TVars>(write: (vars: TVars) => Promise<unknown>) {
  const { projectId, scriptVersionId } = usePlan();
  const queryClient = useQueryClient();
  const reread = () => queryClient.invalidateQueries({ queryKey: productionPlanKey(projectId, scriptVersionId) });

  return useMutation({
    mutationFn: write,
    onSuccess: reread,
    onError: async (error) => {
      if (error instanceof ApiError && error.status === 409) await reread();
    },
  });
}

/** A write's failure in words, or null. */
export function writeError(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (error instanceof Error && error.message !== '') return error.message;

  return 'That could not be saved.';
}
